import {
  Button,
  List,
  Modal,
  type ModalProps,
  Stack,
  Typography,
} from '@omnipresentgroup/design-system';
import { useXeroConnection } from '@omnipresentgroup/domains';
import { toSentenceCase } from '@omnipresentgroup/helpers';
import {
  type InvoiceSyncAbortedReason,
  type SyncAbortedStatusReasonEntity,
  SyncAbortedStatusReasonType,
} from '@omnipresentgroup/types';
import { useNavigate } from 'react-router';

import { INTEGRATIONS_PATH, XERO_CONFIGURATION_PATH } from '../../../constants';
import { useManagerUser } from '../../../hooks';

const errorMessages: Record<
  SyncAbortedStatusReasonType,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (arguments_?: any | any[]) => JSX.Element
> = {
  [SyncAbortedStatusReasonType.ORGANISATION_MAPPING_NOT_FOUND]: () => (
    <>
      <Typography as="span" size="16">
        Xero organisation not found. The organisation may have been disconnected
        or does not exist.
      </Typography>
      <Typography as="span" size="16">
        Please verify your Xero settings and try again. If the issue persists,
        contact support.
      </Typography>
    </>
  ),
  [SyncAbortedStatusReasonType.CONTACT_NOT_ACTIVE]: (
    contact?: SyncAbortedStatusReasonEntity,
  ) => (
    <>
      <Typography as="span" size="16">
        The contact{' '}
        <Typography as="span" size="16" weight="medium">
          {contact?.details?.name}{' '}
        </Typography>
        associated with the invoice is not active in Xero.
      </Typography>
      <Typography as="span" size="16">
        Please verify your Xero settings and try again. If the issue persists,
        contact support.
      </Typography>
    </>
  ),
  [SyncAbortedStatusReasonType.CATEGORY_MAPPINGS_MISSING]: (
    categories?: SyncAbortedStatusReasonEntity[],
  ) => (
    <>
      <Typography as="span" size="16">
        Category mappings are missing.
      </Typography>
      {categories && (
        <List
          showDisc
          gap="0"
          items={categories.filter((category) => category.details?.name)}
          keyExtractor="id"
          render={(item) => (
            <Typography as="span" size="14">
              {toSentenceCase(item.details?.name ?? '')}
            </Typography>
          )}
        />
      )}
      <Typography as="span" size="16">
        Please verify your category mappings and try again.
      </Typography>
    </>
  ),
  [SyncAbortedStatusReasonType.INVOICE_NUMBER_ALREADY_EXISTS]: () => (
    <>
      <Typography as="span" size="16">
        Invoice number already exists in Xero.
      </Typography>
      <Typography as="span" size="16">
        Please verify the invoice number and try again. If the issue persists,
        contact support.
      </Typography>
    </>
  ),
  [SyncAbortedStatusReasonType.ACCOUNT_NOT_VALID_FOR_DOCUMENT]: (
    account?: SyncAbortedStatusReasonEntity,
  ) => (
    <>
      <Typography as="span" size="16">
        The account{' '}
        <Typography as="span" size="16" weight="medium">
          {account?.details?.code ? `${account.details.code} - ` : ''}
          {account?.details?.name}{' '}
        </Typography>
        associated with the document is not valid.
      </Typography>
      <Typography as="span" size="16">
        Please verify the account details and try again. If the issue persists,
        contact support.
      </Typography>
    </>
  ),
  [SyncAbortedStatusReasonType.ACCOUNT_ARCHIVED]: (
    account?: SyncAbortedStatusReasonEntity,
  ) => (
    <>
      <Typography as="span" size="16">
        The account{' '}
        <Typography as="span" size="16" weight="medium">
          {account?.details?.code ? `${account.details.code} - ` : ''}
          {account?.details?.name}{' '}
        </Typography>
        associated with the document is archived.
      </Typography>
      <Typography as="span" size="16">
        Please verify the account details and try again. If the issue persists,
        contact support.
      </Typography>
    </>
  ),
  [SyncAbortedStatusReasonType.TAX_MISMATCH]: () => (
    <Typography as="span" size="16">
      Please verify the tax settings and try again. If the issue persists,
      contact support.
    </Typography>
  ),
  [SyncAbortedStatusReasonType.UNKNOWN]: () => (
    <>
      <Typography as="span" size="16">
        An unexpected error has occurred.
      </Typography>
      <Typography as="span" size="16">
        Please try again. If the issue persists, contact support.
      </Typography>
    </>
  ),
};

const getReasonDescription = (reason?: InvoiceSyncAbortedReason) => {
  switch (reason?.type) {
    case SyncAbortedStatusReasonType.ORGANISATION_MAPPING_NOT_FOUND: {
      return errorMessages[
        SyncAbortedStatusReasonType.ORGANISATION_MAPPING_NOT_FOUND
      ]();
    }
    case SyncAbortedStatusReasonType.CONTACT_NOT_ACTIVE: {
      return errorMessages[SyncAbortedStatusReasonType.CONTACT_NOT_ACTIVE](
        reason.xeroContact,
      );
    }
    case SyncAbortedStatusReasonType.CATEGORY_MAPPINGS_MISSING: {
      return errorMessages[
        SyncAbortedStatusReasonType.CATEGORY_MAPPINGS_MISSING
      ](reason.categories);
    }
    case SyncAbortedStatusReasonType.INVOICE_NUMBER_ALREADY_EXISTS: {
      return errorMessages[
        SyncAbortedStatusReasonType.INVOICE_NUMBER_ALREADY_EXISTS
      ]();
    }
    case SyncAbortedStatusReasonType.ACCOUNT_NOT_VALID_FOR_DOCUMENT: {
      return errorMessages[
        SyncAbortedStatusReasonType.ACCOUNT_NOT_VALID_FOR_DOCUMENT
      ](reason.xeroAccount);
    }
    case SyncAbortedStatusReasonType.ACCOUNT_ARCHIVED: {
      return errorMessages[SyncAbortedStatusReasonType.ACCOUNT_ARCHIVED](
        reason.xeroAccount,
      );
    }
    case SyncAbortedStatusReasonType.TAX_MISMATCH: {
      return errorMessages[SyncAbortedStatusReasonType.TAX_MISMATCH]();
    }
    default: {
      return errorMessages[SyncAbortedStatusReasonType.UNKNOWN]();
    }
  }
};

const Action = ({ reason }: { reason?: InvoiceSyncAbortedReason }) => {
  const { companyId } = useManagerUser();
  const { data: connectionStatus } = useXeroConnection({
    clientId: companyId,
    enabled: !!companyId,
    throwOnError: false,
  });
  const navigate = useNavigate();

  switch (reason?.type) {
    case SyncAbortedStatusReasonType.ORGANISATION_MAPPING_NOT_FOUND: {
      return (
        <Button onClick={() => navigate(INTEGRATIONS_PATH)}>Connect</Button>
      );
    }
    case SyncAbortedStatusReasonType.CONTACT_NOT_ACTIVE: {
      return (
        reason.xeroContact?.details?.url && (
          <Button
            onClick={() =>
              window.open(reason.xeroContact?.details?.url, '_blank')
            }
          >
            Review contact
          </Button>
        )
      );
    }
    case SyncAbortedStatusReasonType.CATEGORY_MAPPINGS_MISSING: {
      const canConfigure =
        connectionStatus?.canUserConfigureConnection || false;

      return (
        canConfigure && (
          <Button
            onClick={() =>
              navigate(XERO_CONFIGURATION_PATH, {
                state: {
                  from: 'InvoiceReports',
                },
              })
            }
          >
            Map categories
          </Button>
        )
      );
    }
    case SyncAbortedStatusReasonType.INVOICE_NUMBER_ALREADY_EXISTS: {
      return (
        reason.xeroOrganisation?.details?.url && (
          <Button
            onClick={() =>
              window.open(reason.xeroOrganisation?.details?.url, '_blank')
            }
          >
            Review organisation
          </Button>
        )
      );
    }
    case SyncAbortedStatusReasonType.ACCOUNT_NOT_VALID_FOR_DOCUMENT: {
      return (
        reason.xeroAccount?.details?.url && (
          <Button
            onClick={() =>
              window.open(reason.xeroAccount?.details?.url, '_blank')
            }
          >
            Review account
          </Button>
        )
      );
    }
    case SyncAbortedStatusReasonType.ACCOUNT_ARCHIVED: {
      return (
        reason.xeroAccount?.details?.url && (
          <Button
            onClick={() =>
              window.open(reason.xeroAccount?.details?.url, '_blank')
            }
          >
            Review account
          </Button>
        )
      );
    }
    case SyncAbortedStatusReasonType.TAX_MISMATCH: {
      const canConfigure =
        connectionStatus?.canUserConfigureConnection || false;

      return (
        canConfigure && (
          <Button
            onClick={() =>
              navigate(XERO_CONFIGURATION_PATH, {
                state: {
                  from: 'InvoiceReports',
                },
              })
            }
          >
            Configure
          </Button>
        )
      );
    }
    default: {
      // eslint-disable-next-line react/jsx-no-useless-fragment
      return <></>;
    }
  }
};

export const XeroErrorModal = ({
  modalProps,
  invoiceSyncStatusReason,
}: {
  modalProps: Omit<ModalProps, 'children'>;
  handleClose: () => void;
  invoiceSyncStatusReason?: InvoiceSyncAbortedReason;
}) => {
  return (
    <Modal {...modalProps} span={4}>
      <Modal.Header
        title={toSentenceCase(
          invoiceSyncStatusReason?.type ?? 'Xero integration aborted',
        )}
      />

      <Modal.Body>
        <Stack justify="space-between">
          {getReasonDescription(invoiceSyncStatusReason)}
        </Stack>
      </Modal.Body>

      <Modal.Footer>
        <Action reason={invoiceSyncStatusReason} />
      </Modal.Footer>
    </Modal>
  );
};
